import { Controller } from "@hotwired/stimulus"

/**
 * Fullscreen Story Viewer — WhatsApp/Instagram-like experience.
 *
 * Features:
 * - Progress bars per slide with auto-advance
 * - Tap left/right edges to navigate
 * - Click center to pause/resume (Facebook-style)
 * - Per-slide reactions and replies
 * - Keyboard navigation (arrows + escape)
 * - 3-level volume control for audio tracks and video
 *   (Full → Low → Mute, keyboard M to cycle)
 */
export default class extends Controller {
  static targets = [
    "progressContainer", "slideContent", "authorAvatar",
    "authorName", "timeAgo", "schoolName", "replyInput",
    "likeBtn", "likeIcon",
    "pauseIndicator", "pauseIcon",
    "downloadBtn",
  ]
  static values = {
    stories: Array,
    csrf: String,
    basePath: String,
  }

  connect() {
    this.currentStoryIndex = 0
    this.currentSlideIndex = 0
    this.timer = null
    this.elapsed = 0
    this.paused = false
    this.volumeLevel = 1.0 // 1.0 = full, 0.3 = low, 0 = mute
    this.filteredStories = []

    // Keyboard navigation
    this.handleKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.stopTimer()
    document.removeEventListener("keydown", this.handleKeydown)
    document.body.style.overflow = ""
  }

  // Called externally from story-bar controller
  openWithStories(storyIdsStr) {
    const storyIds = storyIdsStr.split(",").map(Number)
    const allStories = this.storiesValue

    // Filter and order stories matching the ids
    this.filteredStories = storyIds
      .map(id => allStories.find(s => s.id === id))
      .filter(Boolean)

    if (this.filteredStories.length === 0) return

    // Find first unviewed story, or start from beginning
    this.currentStoryIndex = this.filteredStories
      .findIndex(s => !s.viewed)
    if (this.currentStoryIndex === -1) this.currentStoryIndex = 0
    this.currentSlideIndex = 0

    this.show()
  }

  show() {
    this.paused = false
    this.volumeLevel = 1.0
    this.element.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this.handleKeydown)
    this.hidePauseIndicator()
    this.renderCurrentSlide()
    this.startTimer()
  }

  close() {
    this.stopTimer()
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this.handleKeydown)
    this.updateFlashInfoBadge()
  }

  // --- Navigation ---

  nextSlide() {
    if (this.paused) return
    const story = this.currentStory
    if (!story) return

    if (this.currentSlideIndex < story.slides.length - 1) {
      this.currentSlideIndex++
      this.renderCurrentSlide()
      this.resetTimer()
    } else {
      this.nextStory()
    }
  }

  prevSlide() {
    if (this.paused) return
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--
      this.renderCurrentSlide()
      this.resetTimer()
    } else {
      this.prevStory()
    }
  }

  nextStory() {
    if (this.currentStoryIndex < this.filteredStories.length - 1) {
      this.recordView()
      this.currentStoryIndex++
      this.currentSlideIndex = 0
      this.renderCurrentSlide()
      this.resetTimer()
    } else {
      this.recordView()
      this.close()
    }
  }

  prevStory() {
    if (this.currentStoryIndex > 0) {
      this.currentStoryIndex--
      this.currentSlideIndex = 0
      this.renderCurrentSlide()
      this.resetTimer()
    }
  }

  // --- Pause / Resume ---

  togglePause() {
    if (this.paused) {
      this.resume()
    } else {
      this.pause()
    }
  }

  pause() {
    this.paused = true
    this.showPauseIndicator()
    const video = this.element.querySelector("video")
    if (video) video.pause()
    if (this._currentAudio) this._currentAudio.pause()
  }

  resume() {
    this.paused = false
    this.hidePauseIndicator()
    const video = this.element.querySelector("video")
    if (video) video.play().catch(() => {})
    if (this._currentAudio && this.volumeLevel > 0) {
      this._currentAudio.play().catch(() => {})
    }
  }

  showPauseIndicator() {
    if (!this.hasPauseIndicatorTarget) return
    // Show pause icon
    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.innerHTML =
        '<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>'
    }
    this.pauseIndicatorTarget.classList.remove("hidden")
  }

  hidePauseIndicator() {
    if (!this.hasPauseIndicatorTarget) return
    this.pauseIndicatorTarget.classList.add("hidden")
  }

  // --- Volume control with slider ---

  applyVolume() {
    const video = this.element.querySelector("video")
    if (video) {
      video.volume = this.volumeLevel
      video.muted = this.volumeLevel === 0
    }

    if (this._currentAudio) {
      this._currentAudio.volume = this.volumeLevel
      this._currentAudio.muted = this.volumeLevel === 0
    }
  }

  volumeIcon() {
    if (this.volumeLevel === 0) {
      return `<svg class="w-5 h-5" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5.586 15H4a1 1 0
                         01-1-1v-4a1 1 0 011-1h1.586l4.707
                         -4.707C10.923 3.663 12 4.109 12
                         5v14c0 .891-1.077 1.337-1.707
                         .707L5.586 15z"/>
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 14l2-2m0 0l2-2m-2
                         2l-2-2m2 2l2 2"/>
              </svg>`
    }
    if (this.volumeLevel <= 0.5) {
      return `<svg class="w-5 h-5" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.536 8.464a5 5 0
                         010 7.072M5.586 15H4a1 1 0
                         01-1-1v-4a1 1 0 011-1h1.586l4.707
                         -4.707C10.923 3.663 12 4.109 12
                         5v14c0 .891-1.077 1.337-1.707
                         .707L5.586 15z"/>
              </svg>`
    }
    return `<svg class="w-5 h-5" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15.536 8.464a5 5 0
                       010 7.072m2.828-9.9a9 9 0
                       010 12.728M5.586 15H4a1 1 0
                       01-1-1v-4a1 1 0 011-1h1.586l4.707
                       -4.707C10.923 3.663 12 4.109 12
                       5v14c0 .891-1.077 1.337-1.707
                       .707L5.586 15z"/>
            </svg>`
  }

  // Builds the volume control via DOM API
  // (direct event listeners — more reliable than
  // data-action on innerHTML-created elements)
  addVolumeControl(container) {
    const stop = (e) => e.stopPropagation()

    // Wrapper
    const wrap = document.createElement("div")
    wrap.className =
      "absolute top-14 right-4 z-30 " +
      "flex flex-col items-center gap-1"
    wrap.addEventListener("click", stop)
    wrap.addEventListener("mousedown", stop)
    wrap.addEventListener("touchstart", stop)

    // Icon button
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className =
      "w-9 h-9 rounded-full bg-black/50 " +
      "flex items-center justify-center " +
      "text-white hover:bg-black/70 transition-colors"
    btn.innerHTML = this.volumeIcon()

    // Slider popup (hidden initially)
    const popup = document.createElement("div")
    popup.className =
      "hidden bg-black/60 backdrop-blur-sm " +
      "rounded-full py-3 px-1.5 " +
      "flex flex-col items-center"
    popup.style.height = "120px"

    const input = document.createElement("input")
    input.type = "range"
    input.min = "0"
    input.max = "100"
    input.value = String(
      Math.round(this.volumeLevel * 100)
    )
    input.className = "accent-white cursor-pointer"
    input.style.cssText =
      "writing-mode: vertical-lr; direction: rtl; " +
      "height: 100px; width: 24px;"

    input.addEventListener("input", (e) => {
      e.stopPropagation()
      this.volumeLevel = Number(e.target.value) / 100
      this.applyVolume()
      btn.innerHTML = this.volumeIcon()
    })

    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      e.preventDefault()
      popup.classList.toggle("hidden")
    })

    popup.appendChild(input)
    wrap.appendChild(btn)
    wrap.appendChild(popup)
    container.appendChild(wrap)
    this._volumePopup = popup
  }

  // --- Rendering ---

  get currentStory() {
    return this.filteredStories[this.currentStoryIndex]
  }

  get currentSlide() {
    const story = this.currentStory
    if (!story) return null
    return story.slides[this.currentSlideIndex]
  }

  renderCurrentSlide() {
    const story = this.currentStory
    const slide = this.currentSlide
    if (!story || !slide) return

    // Author info
    this.authorNameTarget.textContent = story.author?.name || ""
    this.timeAgoTarget.textContent = this.formatTimeAgo(
      story.published_at
    )

    // School the story belongs to (multi-school feed)
    if (this.hasSchoolNameTarget) {
      const school = story.school_name || ""
      this.schoolNameTarget.textContent = school
      this.schoolNameTarget.classList.toggle("hidden", !school)
    }

    // Avatar
    if (story.author?.avatar_url) {
      const avatarUrl = this.sanitizeUrl(story.author.avatar_url)
      const avatarAlt = this.escapeHtml(story.author.name || "")
      this.authorAvatarTarget.innerHTML =
        `<img src="${avatarUrl}"
              class="w-full h-full object-cover rounded-full"
              alt="${avatarAlt}">`
    } else {
      const initials = this.escapeHtml(
        (story.author?.name || "?")
          .split(" ").map(n => n[0]).join("")
      )
      this.authorAvatarTarget.innerHTML =
        `<div class="w-full h-full rounded-full bg-white/30
                     flex items-center justify-center">
          <span class="text-white text-[12px] font-bold">
            ${initials}
          </span>
        </div>`
    }

    // Progress bars
    this.renderProgressBars(story.slides.length)

    // Slide content
    this.renderSlideContent(slide)

    // Highlight active like for this slide
    this.updateLikeButton(slide.my_reaction)
  }

  renderProgressBars(total) {
    const container = this.progressContainerTarget
    container.innerHTML = ""

    for (let i = 0; i < total; i++) {
      const bar = document.createElement("div")
      bar.className =
        "flex-1 h-[3px] rounded-full bg-white/30 overflow-hidden"

      const fill = document.createElement("div")
      fill.className = "h-full rounded-full transition-all"
      fill.dataset.slideIndex = i

      if (i < this.currentSlideIndex) {
        fill.className += " bg-white w-full"
      } else if (i === this.currentSlideIndex) {
        fill.className += " bg-white"
        fill.style.width = "0%"
        fill.dataset.storyViewerTarget = "activeProgress"
      } else {
        fill.className += " bg-white/0 w-0"
      }

      bar.appendChild(fill)
      container.appendChild(bar)
    }
  }

  renderSlideContent(slide) {
    const container = this.slideContentTarget
    let html = ""
    const hasAudio = slide.audio_url || slide.slide_type === "video"

    switch (slide.slide_type) {
    case "image":
      html = `<img src="${this.sanitizeUrl(slide.media_url)}"
                   class="max-w-full max-h-full object-contain"
                   alt="${this.escapeHtml(slide.alt_text || "")}">`
      if (slide.body) {
        html += this.buildTextOverlay(slide)
      }
      break

    case "video":
      html = `<video src="${this.sanitizeUrl(slide.media_url)}"
                     class="max-w-full max-h-full object-contain"
                     autoplay playsinline
                     data-story-viewer-target="video">
              </video>`
      if (slide.body) {
        html += this.buildTextOverlay(slide)
      }
      break

    case "text_only":
      html = this.buildTextSlide(slide)
      break

    case "audio":
      html = this.buildAudioSlide(slide)
      break

    default:
      html = this.buildTextSlide(slide)
    }

    container.innerHTML = html

    // Add volume control (via DOM, not innerHTML)
    if (hasAudio) {
      this.addVolumeControl(container)
    }

    // Show/hide download button for image/video slides
    this.updateDownloadButton(slide)

    // Stop previous audio
    if (this._currentAudio) {
      this._currentAudio.pause()
      this._currentAudio = null
    }

    // Play audio track if present (for non-video slides)
    if (slide.audio_url) {
      const audio = new Audio(slide.audio_url)
      audio.volume = this.volumeLevel
      audio.muted = this.volumeLevel === 0
      audio.play().catch(() => {})
      this._currentAudio = audio
    }

    // Apply volume to video if present
    this.applyVolume()
  }

  buildTextSlide(slide) {
    const style = slide.text_style || {}
    const bg = style.background_color || "#1a1a2e"
    const color = style.text_color || "#ffffff"
    const fontSize = style.font_size || "24px"
    const align = style.text_align || "center"

    return `<div class="w-full h-full flex items-center
                        justify-center p-8"
                 style="background-color: ${bg}">
              <p style="color: ${color}; font-size: ${fontSize};
                        text-align: ${align}; line-height: 1.4;
                        max-width: 90%; word-wrap: break-word;">
                ${this.escapeHtml(slide.body || "")}
              </p>
            </div>`
  }

  buildTextOverlay(slide) {
    const style = slide.text_style || {}
    const color = style.text_color || "#ffffff"
    return `<div class="absolute bottom-20 left-4 right-4 z-10">
              <p class="text-[16px] leading-relaxed drop-shadow-lg"
                 style="color: ${color}">
                ${this.escapeHtml(slide.body)}
              </p>
            </div>`
  }

  buildAudioSlide(slide) {
    return `<div class="w-full h-full flex flex-col items-center
                        justify-center gap-4 bg-gradient-to-b
                        from-purple-900 to-indigo-900">
              <div class="w-24 h-24 rounded-full bg-white/20
                          flex items-center justify-center
                          animate-pulse">
                <svg class="w-12 h-12 text-white" fill="currentColor"
                     viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21
                           0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
              <p class="text-white text-[16px]">
                ${this.escapeHtml(slide.body || "")}
              </p>
              <audio src="${this.sanitizeUrl(slide.media_url || slide.audio_url)}"
                     autoplay
                     data-story-viewer-target="audio">
              </audio>
            </div>`
  }

  // --- Download ---

  updateDownloadButton(slide) {
    if (!this.hasDownloadBtnTarget) return

    const isMedia =
      slide.slide_type === "image" ||
      slide.slide_type === "video"

    if (isMedia && slide.media_url) {
      this._currentMediaDownloadUrl =
        slide.media_download_url || slide.media_url
      this.downloadBtnTarget.classList.remove("hidden")
    } else {
      this._currentMediaDownloadUrl = null
      this.downloadBtnTarget.classList.add("hidden")
    }
  }

  downloadMedia() {
    const url = this._currentMediaDownloadUrl
    if (!url) return

    // Use a direct link instead of fetch() to avoid CORS
    // issues when ActiveStorage redirects to S3/GCS.
    // The URL uses disposition=attachment so the browser
    // downloads the file instead of displaying it.
    const a = document.createElement("a")
    a.href = url
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  // --- Timer / Progress ---

  startTimer() {
    this.elapsed = 0
    const slide = this.currentSlide
    if (!slide) return

    const duration = (slide.duration_seconds || 5) * 1000
    const interval = 50

    this.timer = setInterval(() => {
      if (this.paused) return

      this.elapsed += interval
      const progress =
        Math.min((this.elapsed / duration) * 100, 100)

      const activeBar = this.element.querySelector(
        "[data-story-viewer-target='activeProgress']"
      )
      if (activeBar) {
        activeBar.style.width = `${progress}%`
      }

      if (this.elapsed >= duration) {
        this.nextSlide()
      }
    }, interval)
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    // Stop video
    const video = this.element.querySelector("video")
    if (video) {
      video.pause()
      video.removeAttribute("src")
      video.load()
    }
    // Stop audio
    if (this._currentAudio) {
      this._currentAudio.pause()
      this._currentAudio = null
    }
  }

  resetTimer() {
    this.stopTimer()
    this.startTimer()
  }

  // --- Interactions ---

  toggleLike() {
    const story = this.currentStory
    const slide = this.currentSlide
    if (!story || !slide) return

    const isLiked = !!slide.my_reaction

    if (isLiked) {
      // Unlike — DELETE to unreact endpoint
      fetch(`${this.basePathValue}/${story.id}/unreact`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue,
        },
        body: JSON.stringify({ slide_id: slide.id }),
      })
        .then(() => {
          slide.my_reaction = null
          this.updateLikeButton(null)
        })
        .catch(() => {})
    } else {
      // Like — POST to react endpoint
      fetch(`${this.basePathValue}/${story.id}/react`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue,
        },
        body: JSON.stringify({
          reaction_type: "love",
          slide_id: slide.id,
        }),
      })
        .then(() => {
          slide.my_reaction = "love"
          this.updateLikeButton("love")
        })
        .catch(() => {})
    }
  }

  sendReply() {
    const input = this.replyInputTarget
    const body = input.value.trim()
    if (!body) return

    const story = this.currentStory
    const slide = this.currentSlide
    if (!story || !slide) return

    fetch(`${this.basePathValue}/${story.id}/reply`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfValue,
      },
      body: JSON.stringify({
        body,
        slide_id: slide.id,
      }),
    })
      .then(() => {
        input.value = ""
        this.resume()
      })
      .catch(() => this.resume())
  }

  recordView() {
    const story = this.currentStory
    if (!story) return

    // Mark as viewed in local data
    story.viewed = true
    const global = this.storiesValue.find(
      s => s.id === story.id
    )
    if (global) global.viewed = true

    const slide = this.currentSlide
    const completed = this.currentSlideIndex ===
      story.slides.length - 1

    fetch(`${this.basePathValue}/${story.id}/record_view`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfValue,
      },
      body: JSON.stringify({
        position: slide?.position || 0,
        completed,
      }),
    }).catch(() => {})
  }

  updateLikeButton(activeType) {
    if (!this.hasLikeIconTarget) return

    const icon = this.likeIconTarget
    if (activeType) {
      // Liked — filled heart, green
      icon.setAttribute("fill", "#22C55E")
      icon.setAttribute("stroke", "#22C55E")
    } else {
      // Not liked — outline heart, white
      icon.setAttribute("fill", "none")
      icon.setAttribute("stroke", "white")
    }
  }

  // --- Keyboard ---

  onKeydown(event) {
    switch (event.key) {
    case "ArrowRight":
      this.nextSlide()
      break
    case "ArrowLeft":
      this.prevSlide()
      break
    case "Escape":
      this.close()
      break
    case " ":
      event.preventDefault()
      this.togglePause()
      break
    case "m":
    case "M":
      if (this._volumePopup) {
        this._volumePopup.classList.toggle("hidden")
      }
      break
    }
  }

  // --- Badge update ---

  updateFlashInfoBadge() {
    // Mark viewed stories in the global data
    this.filteredStories.forEach(s => { s.viewed = true })

    // Check if ALL stories (not just filtered) are viewed
    const allViewed = this.storiesValue.every(s => s.viewed)

    if (allViewed) {
      // Hide the green badge on the flash info header icon
      const badge = document.querySelector(
        "[data-flash-info-badge]"
      )
      if (badge) badge.remove()
    }

    // Update author circle rings to gray (viewed)
    const storyBars = document.querySelectorAll(
      "[data-controller='story-bar']"
    )
    storyBars.forEach(bar => {
      const buttons = bar.querySelectorAll(
        "[data-story-bar-story-ids-param]"
      )
      buttons.forEach(btn => {
        const ids = btn.dataset
          .storyBarStoryIdsParam.split(",").map(Number)
        const allBtnViewed = ids.every(
          id => this.storiesValue.find(
            s => s.id === id
          )?.viewed
        )
        if (allBtnViewed) {
          const ring = btn.querySelector(
            ".bg-gradient-to-tr"
          )
          if (ring) {
            ring.classList.remove(
              "bg-gradient-to-tr", "from-amber-400",
              "via-red-500", "to-purple-600",
              "from-green-400", "via-emerald-500",
              "to-teal-600"
            )
            ring.classList.add("bg-gray-300")
          }
        }
      })
    })
  }

  // --- Utilities ---

  formatTimeAgo(isoDate) {
    if (!isoDate) return ""
    const diff = Date.now() - new Date(isoDate).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.floor(hours / 24)}d`
  }

  escapeHtml(text) {
    const el = document.createElement("div")
    el.textContent = text
    return el.innerHTML
  }

  sanitizeUrl(url) {
    if (!url) return ""
    // Only allow relative paths starting with /
    if (url.startsWith("/")) return url
    try {
      const parsed = new URL(url, window.location.origin)
      if (parsed.origin === window.location.origin) return url
    } catch { /* invalid URL */ }
    return ""
  }
}
